import React, { useEffect, useRef } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import { whatsAppMediaUrl } from "../../whatsappApi";

/**
 * MessagePane — one conversation's history.
 *
 * The window indicator in the header is the most important affordance here:
 * outside the 24-hour service window WhatsApp refuses free-form messages, and
 * a composer that looks usable and then errors reads as a bug.
 */

const STATUS_ICON = {
  queued: { icon: "fa-clock", label: "Queued" },
  sent: { icon: "fa-check", label: "Sent" },
  delivered: { icon: "fa-check-double", label: "Delivered" },
  read: { icon: "fa-check-double", label: "Read" },
  failed: { icon: "fa-circle-exclamation", label: "Failed" },
};

function formatTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function remaining(expiresAt) {
  if (!expiresAt) return "";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "";
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}

function MediaBlock({ practiceNumber, message }) {
  const { media } = message;
  if (!media) return null;

  if (!media.fileStorageId) {
    return (
      <span className="wa-bubble-meta">
        {media.rejectedReason || "Attachment loading…"}
      </span>
    );
  }

  const url = whatsAppMediaUrl(practiceNumber, message.waMessageId);

  if (String(media.mimeType || "").startsWith("image/")) {
    return <img className="wa-bubble-media" src={url} alt={media.filename || "Attachment"} />;
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="d-block mb-1">
      <i className="fa-solid fa-file-arrow-down me-2" />
      {media.filename || "Attachment"}
    </a>
  );
}

function Bubble({ message, practiceNumber, onRetry }) {
  const outbound = message.direction === "outbound";
  const status = STATUS_ICON[message.status];

  return (
    <div className={`wa-bubble-row ${outbound ? "wa-bubble-row-out" : ""}`}>
      <div
        className={[
          "wa-bubble",
          outbound ? "wa-bubble-out" : "wa-bubble-in",
          message.status === "failed" ? "wa-bubble-failed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <MediaBlock practiceNumber={practiceNumber} message={message} />

        {message.type === "unsupported" ? (
          <span className="fst-italic">{message.body}</span>
        ) : (
          message.body
        )}

        <span className="wa-bubble-meta">
          {message.templateName && (
            <Badge bg="secondary" className="me-2">
              Template
            </Badge>
          )}
          {outbound && message.sentByName ? `${message.sentByName} · ` : ""}
          {formatTime(message.createdAt)}
          {outbound && status && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {message.status === "failed" && message.errorMessage
                    ? message.errorMessage
                    : status.label}
                </Tooltip>
              }
            >
              <i className={`fa-solid ${status.icon} ms-2`} />
            </OverlayTrigger>
          )}
        </span>

        {message.status === "failed" && onRetry && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={() => onRetry(message)}
          >
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

export default function MessagePane({
  practiceNumber,
  conversation,
  messages,
  loading,
  onBack,
  onViewPatient,
  onRetry,
}) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  if (!conversation) {
    return (
      <div className="wa-message-scroll d-flex align-items-center justify-content-center">
        <p className="mb-0 text-muted">Select a conversation.</p>
      </div>
    );
  }

  const name =
    conversation.patientName ||
    conversation.profileName ||
    conversation.displayPhone;

  return (
    <>
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
        {onBack && (
          <Button
            variant="link"
            className="text-primary text-decoration-none p-0 d-md-none"
            onClick={onBack}
            aria-label="Back to conversations"
          >
            <i className="fa-solid fa-chevron-left" />
          </Button>
        )}

        <div className="min-w-0">
          <div className="fw-semibold text-primary text-truncate">{name}</div>
          <small className="text-muted">{conversation.displayPhone}</small>
        </div>

        <div className="ms-auto d-flex align-items-center gap-2">
          {conversation.windowOpen ? (
            <Badge bg="success">
              Open · {remaining(conversation.windowExpiresAt)}
            </Badge>
          ) : (
            <Badge bg="secondary">Window closed</Badge>
          )}

          {conversation.patientId && onViewPatient && (
            <OverlayTrigger placement="top" overlay={<Tooltip>View patient</Tooltip>}>
              <Button
                variant="link"
                className="text-primary text-decoration-none p-0"
                style={{ width: 24, height: 24 }}
                onClick={() => onViewPatient(conversation.patientId)}
              >
                <i className="fa-solid fa-user" />
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </div>

      <div className="wa-message-scroll" ref={scrollRef} aria-live="polite">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading messages…</p>
          </div>
        ) : !messages.length ? (
          <p className="text-center text-muted">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <Bubble
              key={m._id || m.waMessageId}
              message={m}
              practiceNumber={practiceNumber}
              onRetry={onRetry}
            />
          ))
        )}
      </div>
    </>
  );
}
